/**
 * DevisModal — demande de devis (V1) depuis une fiche technique.
 *
 * 1. Choix du prestataire dans l'annuaire (TiersPicker filtré fournisseur) —
 *    création à la volée possible.
 * 2. Objet + description des travaux DICTABLES à la voix (Web Speech API).
 * 3. Envoi : mail au prestataire (charte mairie) + copie mairie + trace fiche.
 *
 * V1 : pas de cycle (téléversement/paiement/contrôle) — réservé à G3.
 */
import { useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Button, Alert } from '@/components/ui';
import { Mic, MicOff } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { TiersPicker } from '@/components/annuaire/TiersPicker';
import { useDictee } from '@/hooks/useDictee';
import type { AnnuaireTiers } from '@roronoa-collectivites/shared-types';

export function DevisModal({
  signalementId,
  signalementNumero,
  onClose,
  onDone,
}: {
  signalementId: string;
  signalementNumero: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [tiers, setTiers] = useState<AnnuaireTiers | null>(null);
  const [objet, setObjet] = useState(`Demande de devis — fiche ${signalementNumero}`);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dictee = useDictee('fr-FR', (t) => setDescription((d) => (d ? d + t : t)));

  async function envoyer() {
    if (!tiers) { setError('Choisissez un prestataire'); return; }
    if (!tiers.email) { setError('Ce prestataire n\'a pas d\'email — ajoutez-en un dans l\'annuaire'); return; }
    if (description.trim().length < 3) { setError('Décrivez les travaux'); return; }
    setSaving(true); setError(null);
    try {
      const res = await api.serviceTechnique.signalements.demanderDevis(signalementId, {
        tiersId: tiers.id, tiersNom: tiers.nom, tiersEmail: tiers.email,
        objet: objet.trim(), description: description.trim(),
      });
      dictee.arreter();
      if (res.mailEnvoye) onDone();
      else setError(`Demande enregistrée mais l'email n'est pas parti : ${res.mailErreur ?? 'erreur inconnue'}`);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onClose={() => { dictee.arreter(); onClose(); }} title="Demander un devis" size="lg"
      footer={<>
        <Button variant="ghost" onClick={() => { dictee.arreter(); onClose(); }}>Annuler</Button>
        <Button variant="primary" loading={saving} onClick={() => void envoyer()}>Envoyer la demande</Button>
      </>}>
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        {/* 1. Prestataire */}
        <div>
          <label className="mb-1.5 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Prestataire</label>
          {tiers ? (
            <div className="flex items-center justify-between rounded-r-md border border-tech bg-tech-tint/30 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-ink">{tiers.nom}</span>
                <span className="block truncate text-xs text-ink-subtle">{[tiers.email, tiers.telephone].filter(Boolean).join(' · ') || 'Pas de contact'}</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => setTiers(null)}>Changer</Button>
            </div>
          ) : (
            <TiersPicker
              categorie="fournisseur_prestataire"
              autoriserCreation
              categorieCreation={['fournisseur_prestataire']}
              onSelect={setTiers}
            />
          )}
        </div>

        {/* 2. Objet */}
        <div>
          <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Objet du mail</label>
          <input value={objet} onChange={(e) => setObjet(e.target.value)} className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
        </div>

        {/* 3. Description dictable */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Description des travaux</label>
            {dictee.supporte && (
              <button type="button" onClick={() => dictee.ecoute ? dictee.arreter() : dictee.demarrer()}
                className={`inline-flex items-center gap-1.5 rounded-r-pill px-2.5 py-1 text-xs font-medium transition-colors ${dictee.ecoute ? 'bg-danger text-white' : 'bg-tech text-white'}`}>
                {dictee.ecoute ? <><MicOff className="h-3.5 w-3.5" /> Arrêter</> : <><Mic className="h-3.5 w-3.5" /> Dicter</>}
              </button>
            )}
          </div>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5}
            placeholder={dictee.supporte ? 'Écrivez ou cliquez sur « Dicter »…' : 'Décrivez les travaux à réaliser…'}
            className="w-full rounded-r-md border border-border bg-surface/60 px-2 py-2 text-sm text-ink" />
          {dictee.ecoute && <p className="mt-1 text-xs text-tech-fg">🎙 Écoute en cours… parlez, le texte s'ajoute au fur et à mesure.</p>}
          {!dictee.supporte && <p className="mt-1 text-xs text-ink-subtle">La dictée vocale n'est pas disponible sur ce navigateur — saisie au clavier.</p>}
        </div>

        <p className="text-xs text-ink-subtle">Un mail à la charte de la mairie sera envoyé au prestataire, avec une copie à la mairie. La demande sera tracée sur la fiche.</p>
      </div>
    </Dialog>
  );
}
